import { IRequestContext } from '@msdyn365-commerce/core-internal';
import { useCallback, useEffect, useRef } from 'react';
import { ProductColor, VariantDetails } from '../../relewise/types/mapped';
import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';
import { useDatalayerService } from './useDatalayerService';

interface UsePdpDatalayerParams {
    request: IRequestContext;
    telemetry: ITelemetry;
    productItemId: string | undefined;
    selectedVariant: VariantDetails | undefined;
    selectedColor: ProductColor | undefined;
    selectedSize: string | undefined;
    compatibleSizesByColor: Record<string, string[]> | undefined;
    brand: string | undefined;
}

export const usePdpDatalayer = ({
    request,
    telemetry,
    productItemId,
    selectedVariant,
    selectedColor,
    selectedSize,
    compatibleSizesByColor,
    brand
}: UsePdpDatalayerParams) => {
    const datalayerService = useDatalayerService({ request, telemetry });
    const lastTrackedVariantKeyRef = useRef<string | undefined>();

    // Push view_item event when color or size change.
    // Size may be undefined — fall back to the first available size for the color.
    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        if (!productItemId || !selectedVariant || !selectedColor) {
            return;
        }
        const size = selectedSize ?? compatibleSizesByColor?.[selectedColor.name]?.[0] ?? '';
        const variantKey = `${productItemId}:${selectedColor.name}:${size}`;
        if (variantKey === lastTrackedVariantKeyRef.current) {
            return;
        }
        lastTrackedVariantKeyRef.current = variantKey;
        datalayerService.pushViewItem(productItemId, selectedColor.name, size, selectedVariant, brand);
    }, [datalayerService, productItemId, selectedVariant, selectedColor, selectedSize, compatibleSizesByColor, brand]);

    const trackAddToCart = useCallback(() => {
        if (!productItemId || !selectedVariant || !selectedColor || !selectedSize) {
            return;
        }
        datalayerService.pushAddToCart(productItemId, selectedColor.name, selectedSize, selectedVariant, brand);
    }, [datalayerService, productItemId, selectedVariant, selectedColor, selectedSize, brand]);

    return { trackAddToCart };
};
